import { useEffect, useMemo, useRef, useState } from "react"
import type { LeadStatus } from "@/shared/api/generated/types/marketing"
import { useDebouncedValue } from "@/shared/lib/use-debounced-value"
import type { LeadsPageSize } from "./constants"
import {
	type LeadsRouteSearch,
	normalizeLeadsSearch,
	type UpdateLeadsRouteSearch,
} from "./leads-search"

export function useLeadsFilters(
	routeSearch: LeadsRouteSearch,
	updateRouteSearch: UpdateLeadsRouteSearch,
) {
	const normalizedSearch = useMemo(
		() => normalizeLeadsSearch(routeSearch),
		[routeSearch],
	)
	const [search, setSearch] = useState(normalizedSearch.search)
	const debouncedSearch = useDebouncedValue(search, 400)
	const previousDebouncedSearch = useRef(debouncedSearch)
	const previousRouteSearch = useRef(normalizedSearch.search)
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

	useEffect(() => {
		if (previousRouteSearch.current === normalizedSearch.search) return

		previousRouteSearch.current = normalizedSearch.search
		setSearch(normalizedSearch.search)
	}, [normalizedSearch.search])

	useEffect(() => {
		if (previousDebouncedSearch.current === debouncedSearch) return

		previousDebouncedSearch.current = debouncedSearch
		if (
			debouncedSearch !== normalizedSearch.search &&
			debouncedSearch === search
		) {
			updateRouteSearch({
				search: debouncedSearch || undefined,
				page: undefined,
			})
		}
	}, [debouncedSearch, normalizedSearch.search, search, updateRouteSearch])

	const filterParams = useMemo(
		() => ({
			...(normalizedSearch.search ? { search: normalizedSearch.search } : {}),
			...(normalizedSearch.status ? { status: normalizedSearch.status } : {}),
			...(normalizedSearch.createdFrom
				? { createdFrom: normalizedSearch.createdFrom }
				: {}),
			...(normalizedSearch.createdTo
				? { createdTo: normalizedSearch.createdTo }
				: {}),
			timezone,
		}),
		[normalizedSearch, timezone],
	)

	const setStatus = (value: LeadStatus | "") => {
		updateRouteSearch({ status: value || undefined, page: undefined })
	}
	const setCreatedFrom = (value: string) => {
		updateRouteSearch({ createdFrom: value || undefined, page: undefined })
	}
	const setCreatedTo = (value: string) => {
		updateRouteSearch({ createdTo: value || undefined, page: undefined })
	}
	const setPage = (value: number) => {
		updateRouteSearch({ page: value === 1 ? undefined : value })
	}
	const setPageSize = (value: LeadsPageSize) => {
		updateRouteSearch({
			pageSize: value === 25 ? undefined : value,
			page: undefined,
		})
	}
	const reset = () => {
		setSearch("")
		updateRouteSearch({
			search: undefined,
			status: undefined,
			createdFrom: undefined,
			createdTo: undefined,
			page: undefined,
		})
	}

	return {
		search,
		setSearch,
		status: normalizedSearch.status,
		setStatus,
		createdFrom: normalizedSearch.createdFrom,
		setCreatedFrom,
		createdTo: normalizedSearch.createdTo,
		setCreatedTo,
		page: normalizedSearch.page,
		setPage,
		pageSize: normalizedSearch.pageSize,
		setPageSize,
		filterParams,
		reset,
	}
}

export type LeadsFiltersModel = ReturnType<typeof useLeadsFilters>
